import { intakeUploadSizeError } from "./intakeUploadLimits";

const DEFAULT_RECOVERY =
  "Try a supported format (PDF, Word, PowerPoint, Excel, or plain text), " +
  "or export/save the document again and re-upload.";

const EMPTY_FILE_RECOVERY =
  "Please share the relevant details here in chat, or upload a correct document " +
  "with your application details.";

/** @param {string} name */
function displayName(name) {
  return name || "this document";
}

/**
 * Per-error chat labels, toast text, and assistant messages.
 * @type {Record<string, {
 *   chatLabel: string,
 *   isGuidance?: boolean,
 *   toastMessage: (name: string) => string,
 *   chatMessage: (name: string) => string,
 * }>}
 */
const UPLOAD_ERROR_TEMPLATES = {
  EMPTY_FILE: {
    chatLabel: "Empty document",
    isGuidance: true,
    toastMessage: (name) =>
      `The uploaded document "${name}" is empty and contains no application details.`,
    chatMessage: (name) =>
      `The uploaded document **${displayName(name)}** is empty and contains no application details.\n\n` +
      EMPTY_FILE_RECOVERY,
  },
  CORRUPTED_PDF: {
    chatLabel: "Corrupted PDF",
    toastMessage: (name) => `The uploaded PDF "${name}" is corrupted or damaged.`,
    chatMessage: (name) =>
      `The uploaded PDF **${displayName(name)}** is corrupted or damaged and could not be read.\n\n` +
      "Please open the file locally to confirm it works, then re-export as PDF and upload again.",
  },
  PASSWORD_PROTECTED_PDF: {
    chatLabel: "Password-protected PDF",
    toastMessage: (name) => `The uploaded PDF "${name}" is password-protected.`,
    chatMessage: (name) =>
      `The uploaded PDF **${displayName(name)}** is password-protected and cannot be read.\n\n` +
      "Please remove the password, save an unprotected copy, and upload again.",
  },
  PDF_NO_READABLE_TEXT: {
    chatLabel: "Unsupported embedded objects",
    toastMessage: (name) =>
      `The uploaded PDF "${name}" contains no extractable text (scan-only or unsupported embedded objects).`,
    chatMessage: (name) =>
      `The uploaded PDF **${displayName(name)}** contains no extractable text. ` +
      "It may be scan-only or include unsupported embedded objects.\n\n" +
      "Please use OCR or export a text-based PDF from your editor, then upload again.",
  },
  UNSUPPORTED_ARCHIVE: {
    chatLabel: "ZIP archive not supported",
    toastMessage: (name) => `The uploaded file "${name}" could not be used as an archive.`,
    chatMessage: (name) =>
      `The uploaded file **${displayName(name)}** could not be processed as an archive.\n\n` +
      "Use a .zip that contains PDF, Word, Excel, or text files — or upload those files directly. RAR/7z are not supported.",
  },
  INVALID_FILENAME: {
    chatLabel: "Invalid file name",
    toastMessage: (name) => `The file name "${name}" contains invalid special characters.`,
    chatMessage: (name) =>
      `The file name **${displayName(name)}** contains invalid special characters.\n\n` +
      "Please rename the file using only letters, numbers, spaces, hyphens, and underscores, then upload again.",
  },
  MIXED_CONTENT: {
    chatLabel: "Mixed or unsupported document",
    toastMessage: (name) =>
      `The uploaded document "${name}" uses a mixed or unsupported format and could not be read.`,
    chatMessage: (name) =>
      `The uploaded document **${displayName(name)}** could not be read. ` +
      "It may use a mixed format or combine content types that cannot be processed.\n\n" +
      "Please save or export the document as a single PDF or Word file and upload again.",
  },
  PARSE_FAILED: {
    chatLabel: "Document could not be read",
    toastMessage: (name) => `The uploaded document "${name}" could not be read.`,
    chatMessage: (name) =>
      `The uploaded document **${displayName(name)}** could not be read. ` +
      "The file may be corrupted or in an unsupported format.\n\n" +
      DEFAULT_RECOVERY,
  },
  UNSUPPORTED_TYPE: {
    chatLabel: "Unsupported file format",
    toastMessage: (name) => `Unsupported file format. "${name}" cannot be uploaded.`,
    chatMessage: (name) =>
      `**Unsupported file format**\n\nThe file **${displayName(name)}** is not supported.\n\n` +
      "Please upload a PDF, Word, TXT, JPG, or PNG file.",
  },
  CORRUPTED_DOC: {
    chatLabel: "Corrupted Word document",
    toastMessage: (name) => `The uploaded Word document "${name}" is corrupted or cannot be opened.`,
    chatMessage: (name) =>
      `The uploaded Word document **${displayName(name)}** is corrupted or cannot be opened.\n\n` +
      "Please open the file locally, save a new copy, and upload again.",
  },
  CORRUPTED_DOCX: {
    chatLabel: "Corrupted Word document",
    toastMessage: (name) => `The uploaded Word document "${name}" is corrupted or cannot be opened.`,
    chatMessage: (name) =>
      `The uploaded Word document **${displayName(name)}** is corrupted or cannot be opened.\n\n` +
      "Please open the file locally, save a new copy, and upload again.",
  },
  CORRUPTED_PPTX: {
    chatLabel: "Corrupted PowerPoint file",
    toastMessage: (name) => `The uploaded PowerPoint file "${name}" is corrupted or cannot be opened.`,
    chatMessage: (name) =>
      `The uploaded PowerPoint file **${displayName(name)}** is corrupted or cannot be opened.\n\n` +
      "Please open the file locally, save a new copy, and upload again.",
  },
  CORRUPTED_XLSX: {
    chatLabel: "Corrupted Excel file",
    toastMessage: (name) => `The uploaded Excel file "${name}" is corrupted or cannot be opened.`,
    chatMessage: (name) =>
      `The uploaded Excel file **${displayName(name)}** is corrupted or cannot be opened.\n\n` +
      "Please open the file locally, save a new copy, and upload again.",
  },
  CORRUPTED_OFFICE: {
    chatLabel: "Corrupted Office document",
    toastMessage: (name) => `The uploaded Office document "${name}" is corrupted or cannot be opened.`,
    chatMessage: (name) =>
      `The uploaded Office document **${displayName(name)}** is corrupted or cannot be opened.\n\n` +
      "Please open the file locally, save a new copy, and upload again.",
  },
  FILE_TOO_LARGE: {
    chatLabel: "Upload too large",
    toastMessage: (_name) => "",
    chatMessage: (_name, message, recovery) =>
      `**Upload too large**\n\n${message}\n\n${recovery}`,
  },
};

const CODE_HINTS = {
  EMPTY_FILE: "The uploaded document is empty and contains no application details.",
  UNSUPPORTED_TYPE: "The uploaded file is not a supported file type.",
  UNSUPPORTED_ARCHIVE: "The uploaded file is a compressed archive (ZIP/RAR).",
  PASSWORD_PROTECTED_PDF: "The uploaded PDF is password-protected.",
  CORRUPTED_PDF: "The uploaded PDF is corrupted or damaged.",
  CORRUPTED_DOC: "The uploaded Word document is corrupted.",
  CORRUPTED_DOCX: "The uploaded Word document is corrupted.",
  CORRUPTED_PPTX: "The uploaded PowerPoint file is corrupted.",
  CORRUPTED_XLSX: "The uploaded Excel file is corrupted.",
  CORRUPTED_OFFICE: "The uploaded Office document is corrupted.",
  PDF_NO_READABLE_TEXT: "The uploaded PDF contains no extractable text.",
  INVALID_FILENAME: "The file name contains invalid special characters.",
  MIXED_CONTENT: "The uploaded document uses a mixed or unsupported format.",
  PARSE_FAILED: "The uploaded document could not be read.",
};

function pickString(...candidates) {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim() && c !== "Intake agent error") return c.trim();
  }
  return null;
}

function parseDetail(detail) {
  if (!detail) return null;
  if (typeof detail === "string") return { message: detail };
  if (typeof detail === "object" && !Array.isArray(detail)) {
    return {
      code: detail.code,
      message: detail.message || detail.error,
      recovery: detail.recovery,
      filename: detail.filename,
    };
  }
  return null;
}

function resolveCorruptedOfficeTemplate(code) {
  if (code === "CORRUPTED_DOC" || code === "CORRUPTED_DOCX") return UPLOAD_ERROR_TEMPLATES.CORRUPTED_DOC;
  if (code === "CORRUPTED_PPTX") return UPLOAD_ERROR_TEMPLATES.CORRUPTED_PPTX;
  if (code === "CORRUPTED_XLSX") return UPLOAD_ERROR_TEMPLATES.CORRUPTED_XLSX;
  if (code === "CORRUPTED_OFFICE") return UPLOAD_ERROR_TEMPLATES.CORRUPTED_OFFICE;
  return null;
}

function getUploadErrorTemplate(code) {
  return UPLOAD_ERROR_TEMPLATES[code] || resolveCorruptedOfficeTemplate(code);
}

function buildValidationChatMessage(code, filename, message, recovery) {
  const tmpl = getUploadErrorTemplate(code);
  if (tmpl) return tmpl.chatMessage(filename, message, recovery);
  return buildGenericUploadErrorChatMessage(filename, message, recovery);
}

function buildGenericUploadErrorChatMessage(filename, message, recovery) {
  const fileLine = filename ? `**File:** ${filename}\n\n` : "";
  const action = recovery || DEFAULT_RECOVERY;
  return `${fileLine}${message}\n\n${action}`;
}

/**
 * @param {import('axios').AxiosError} err
 * @param {string} [fallbackFilename]
 */
export function formatIntakeUploadError(err, fallbackFilename = "") {
  const sizeMsg = intakeUploadSizeError(err);
  if (sizeMsg) {
    const recovery = "Choose a smaller document or split content across multiple uploads.";
    return {
      code: "FILE_TOO_LARGE",
      title: "Upload too large",
      message: sizeMsg,
      recovery,
      filename: fallbackFilename,
      chatLabel: "Upload too large",
      isGuidance: false,
      chatMessage: buildValidationChatMessage("FILE_TOO_LARGE", fallbackFilename, sizeMsg, recovery),
    };
  }

  const data = err?.response?.data || {};
  const parsed = parseDetail(data.detail);
  const code = data.code || parsed?.code || "UPLOAD_FAILED";
  const errMsg = String(err?.message || "");
  const status = err?.response?.status;
  const isIntakeSessionLost =
    status === 410 || code === "SESSION_EXPIRED";
  const isTokenIssue =
    status === 401 ||
    /token refresh failed|updateToken timeout/i.test(errMsg);
  const isTimeout =
    err?.code === "ECONNABORTED" ||
    /timeout/i.test(errMsg);
  const isNetworkError =
    !err?.response &&
    (err?.code === "ERR_NETWORK" ||
      /failed to fetch|network error|networkerror/i.test(errMsg));

  if (isNetworkError) {
    return {
      code: "NETWORK_ERROR",
      title: "Connection failed",
      message: "Could not reach the server. The upload did not complete.",
      recovery:
        "Check that the backend is running (npm start in backend/), refresh the page, and try again. " +
        "If you use a VPN or corporate proxy, ensure localhost:5000 and keycloak.validaite.net are reachable.",
      filename: fallbackFilename,
      chatLabel: "Connection failed",
      isGuidance: false,
      chatMessage:
        "**Connection failed**\n\nThe browser could not reach the API or authentication server " +
        "(often shown as *Failed to fetch*). Confirm the backend is running on port 5000, refresh this page, " +
        "and try the upload again.",
    };
  }

  if (isIntakeSessionLost) {
    return {
      code: "SESSION_EXPIRED",
      title: "Intake session lost",
      message: "The intake session was lost while your image was being processed.",
      recovery:
        "Refresh the page to start a new session, then re-upload. Avoid restarting the backend during uploads.",
      filename: fallbackFilename,
      chatLabel: "Session lost",
      isGuidance: false,
      chatMessage:
        "**Intake session lost**\n\nThe server lost your chat session during processing " +
        "(this can happen after a backend restart or a very long wait). " +
        "Refresh the page to reconnect, then upload the image again.",
    };
  }

  if (isTokenIssue) {
    return {
      code: "SESSION_TOKEN_EXPIRED",
      title: "Session expired",
      message: "Your login session expired during document processing.",
      recovery: "Please sign in again and re-upload the document.",
      filename: fallbackFilename,
      chatLabel: "Session expired",
      isGuidance: false,
      chatMessage:
        "**Session expired**\n\nYour login session expired while the document was being processed. " +
        "Please sign in again and re-upload the document.",
    };
  }

  if (isTimeout) {
    const waitedMin = Math.round(
      parseInt(
        process.env.REACT_APP_INTAKE_UPLOAD_POLL_TIMEOUT_MS ||
          process.env.REACT_APP_INTAKE_UPLOAD_TIMEOUT_MS ||
          "3600000",
        10
      ) / 60000
    );
    return {
      code: "EXTRACTION_TIMEOUT",
      title: "Extraction timed out",
      message: `Document extraction took longer than ${waitedMin} minutes.`,
      recovery:
        "Large images and diagrams can take several minutes. Wait a moment and refresh the page — " +
        "your data may still have been captured. Or try PDF/Word instead of a screenshot.",
      filename: fallbackFilename,
      chatLabel: "Extraction timed out",
      isGuidance: false,
      chatMessage:
        "**Extraction timed out**\n\nThe server stopped processing before extraction finished. " +
        "Try a smaller file, split the content, or paste the relevant sections in chat.",
    };
  }

  const filename = pickString(parsed?.filename, data.filename, fallbackFilename) || fallbackFilename;
  const apiMessage =
    pickString(data.error, parsed?.message, data.message, err?.message)
    || CODE_HINTS[code]
    || "Upload failed. The document could not be processed.";
  const recovery = code === "EMPTY_FILE"
    ? (pickString(data.recovery, parsed?.recovery) || EMPTY_FILE_RECOVERY)
    : (pickString(data.recovery, parsed?.recovery) || DEFAULT_RECOVERY);

  const tmpl = getUploadErrorTemplate(code);
  const message = tmpl?.toastMessage?.(filename) || apiMessage;
  const chatLabel = tmpl?.chatLabel || "Upload failed";
  const isGuidance = Boolean(tmpl?.isGuidance);

  return {
    code,
    title: chatLabel,
    message,
    recovery,
    filename,
    chatLabel,
    isGuidance,
    chatMessage: buildValidationChatMessage(code, filename, apiMessage, recovery),
  };
}

export function buildEmptyFileChatMessage(filename) {
  return UPLOAD_ERROR_TEMPLATES.EMPTY_FILE.chatMessage(filename);
}

export function buildUploadErrorChatMessage(filename, message, recovery) {
  return buildGenericUploadErrorChatMessage(filename, message, recovery);
}
